/**
 * How the pet moves (docs/PETS.md), the parts that need no GL: its gaits (the `walk` trot and the `run` gallop, measured
 * by the art in art/hq/anchors.json `gait`), which one a move takes, the speed profile of a move (accelerate, cruise,
 * decelerate onto the spot), the clip rate that keeps the paws planted at a speed, the corners of a cell path rounded
 * off, and a turn at a capped rate. Pure, so test/petGait.test.ts pins it.
 */

/** A locomotion clip's measured gait: seconds a cycle, and the ground its paws cover in one (world units at scale 1). */
export interface Gait { readonly cycle: number; readonly stride: number }
export type GaitName = "walk" | "run";
export type Gaits = Readonly<Record<GaitName, Gait>>;

/** Until the art measures a model (or for one it has not): a trot and a gallop of about the pup's build. */
export const GAIT_FALLBACK: Gaits = { walk: { cycle: 0.5, stride: 0.14 }, run: { cycle: 0.36, stride: 0.3 } };

export const PET_MOVE = {
  /** A move this long or longer (cells, along the way) is run; a shorter one is a trot. */
  runFrom: 0.85,
  /** The clip rate each gait cruises at: the cruise speed is the gait's own speed (stride ÷ cycle × scale) times this. */
  cruiseRate: { walk: 1.1, run: 1.3 },
  /** The clip rate is held in this range (below it the paws slide a little on the first and last steps, unseen in the blend). */
  rate: [0.55, 1.7] as const,
  /** Seconds to reach cruise from a stand, and to stop from it. */
  accel: { walk: 0.22, run: 0.34 },
  /** The corners of a cell path are rounded with this radius (cells), never more than half of either leg. */
  corner: 0.38,
  /** Points that sample one rounded corner. */
  cornerSteps: 6,
  /** The fastest it turns (radians a second): moving, and to face the road once it has landed. */
  turnRate: 7,
  /** A move that starts more than this far off its heading (radians) turns on the spot first, at turnRate. */
  turnFirst: Math.PI / 3,
  /** Cross-fades between idle, trot and gallop, and in and out of a one-shot (seconds). */
  fade: 0.18,
} as const;

/** The model's measured gaits (anchors.json `gait`), each one checked; what is missing or bad falls back. */
export function gaitsOf(measured: unknown): Gaits {
  const m = (measured ?? {}) as Partial<Record<GaitName, Partial<Gait>>>;
  const pick = (name: GaitName): Gait => {
    const g = m[name];
    return g && typeof g.cycle === "number" && typeof g.stride === "number" && g.cycle > 0 && g.stride > 0 ? { cycle: g.cycle, stride: g.stride } : GAIT_FALLBACK[name];
  };
  return { walk: pick("walk"), run: pick("run") };
}

/** The wild boss pup's charge (`boss_run`), measured like the gaits; this until it is. */
export const BOSS_GAIT_FALLBACK: Gait = { cycle: 0.3333, stride: 0.274 };

/** The measured `boss_run` gait (anchors.json `gait.boss_run`), checked, else the fallback. */
export function bossGaitOf(measured: unknown): Gait {
  const g = ((measured ?? {}) as Record<string, Partial<Gait> | undefined>).boss_run;
  return g && typeof g.cycle === "number" && typeof g.stride === "number" && g.cycle > 0 && g.stride > 0 ? { cycle: g.cycle, stride: g.stride } : BOSS_GAIT_FALLBACK;
}

/** A measured gait `name` (anchors.json `gait.<name>`: the tortoise's `hurry`), checked, else null (the model lacks it). */
export function gaitNamed(measured: unknown, name: string): Gait | null {
  const g = ((measured ?? {}) as Record<string, Partial<Gait> | undefined>)[name];
  return g && typeof g.cycle === "number" && typeof g.stride === "number" && g.cycle > 0 && g.stride > 0 ? { cycle: g.cycle, stride: g.stride } : null;
}

/** The gait a move of `length` cells takes: a short shuffle trots, anything longer runs. */
export function gaitFor(length: number): GaitName { return length >= PET_MOVE.runFrom ? "run" : "walk"; }

/** World units a second the gait covers at clip rate 1, drawn at `scale`. */
export function naturalSpeed(g: Gait, scale: number): number { return (g.stride * scale) / g.cycle; }

/** The speed a gait cruises at (world units a second), drawn at `scale`. */
export function cruiseSpeed(name: GaitName, gaits: Gaits, scale: number): number { return naturalSpeed(gaits[name], scale) * PET_MOVE.cruiseRate[name]; }

/** The clip rate that keeps the paws planted at `speed` (world units a second), held in PET_MOVE.rate. */
export function clipRate(speed: number, g: Gait, scale: number): number {
  const [lo, hi] = PET_MOVE.rate;
  return Math.min(hi, Math.max(lo, speed / naturalSpeed(g, scale)));
}

/**
 * A move's speed over time: from `v0` (0 from a stand) up to `cruise` at `cruise / accel` per second, on at it, and down
 * to a stop on the spot at the same rate — or, for a move too short to reach cruise, up and straight back down (a
 * triangle), or for one too short even for that, a firmer stop from `v0`.
 */
export class SpeedProfile {
  readonly duration: number;
  /** The rate up, and the rate down (world units a second, a second). */
  private readonly up: number;
  private readonly down: number;
  private readonly top: number;
  /** Seconds up to the top speed, and at it. */
  private readonly t1: number;
  private readonly hold: number;

  constructor(readonly length: number, cruise: number, accel: number, private readonly v0 = 0) {
    const a = cruise / Math.max(1e-6, accel);
    v0 = Math.min(Math.max(0, v0), cruise);
    this.v0 = v0;
    const need = (cruise * cruise - v0 * v0) / (2 * a) + (cruise * cruise) / (2 * a);
    if (length >= need) {
      this.up = a; this.down = a; this.top = cruise;
      this.t1 = (cruise - v0) / a; this.hold = (length - need) / cruise;
    } else if (length * 2 * a + v0 * v0 >= 2 * v0 * v0) {
      this.up = a; this.down = a; this.top = Math.sqrt((2 * a * length + v0 * v0) / 2);
      this.t1 = (this.top - v0) / a; this.hold = 0;
    } else {
      // already too fast to reach the spot at the usual rate: straight into a firmer stop
      this.up = a; this.top = v0; this.t1 = 0; this.hold = 0; this.down = (v0 * v0) / (2 * Math.max(1e-6, length));
    }
    this.duration = this.t1 + this.hold + this.top / this.down;
  }

  /** The speed `t` seconds in. */
  speedAt(t: number): number {
    if (t <= 0) return t < 0 ? 0 : this.v0;
    if (t >= this.duration) return 0;
    if (t < this.t1) return this.v0 + this.up * t;
    if (t < this.t1 + this.hold) return this.top;
    return this.down * (this.duration - t);
  }

  /** The way covered `t` seconds in (0 … length). */
  distanceAt(t: number): number {
    if (t <= 0) return 0;
    if (t >= this.duration) return this.length;
    if (t < this.t1) return this.v0 * t + 0.5 * this.up * t * t;
    const d1 = this.v0 * this.t1 + 0.5 * this.up * this.t1 * this.t1;
    if (t < this.t1 + this.hold) return d1 + this.top * (t - this.t1);
    const left = this.duration - t;
    return this.length - 0.5 * this.down * left * left;
  }
}

/** A point on the ground (world x / z). */
export interface Pt { readonly x: number; readonly z: number }

/**
 * The way through `points` with every inner corner rounded off (a quadratic curve from `radius` before the corner to
 * `radius` after it, no more than half of either leg), as a denser polyline from the first point to the last.
 */
export function roundPath(points: readonly Pt[], radius: number = PET_MOVE.corner, steps: number = PET_MOVE.cornerSteps): Pt[] {
  if (points.length < 3) return points.map((p) => ({ x: p.x, z: p.z }));
  const out: Pt[] = [{ x: points[0].x, z: points[0].z }];
  for (let i = 1; i < points.length - 1; i++) {
    const a = points[i - 1], c = points[i], b = points[i + 1];
    const la = Math.hypot(c.x - a.x, c.z - a.z), lb = Math.hypot(b.x - c.x, b.z - c.z);
    const r = Math.min(radius, la / 2, lb / 2);
    if (r <= 1e-6) { out.push({ x: c.x, z: c.z }); continue; }
    const p0 = { x: c.x + ((a.x - c.x) / la) * r, z: c.z + ((a.z - c.z) / la) * r };
    const p2 = { x: c.x + ((b.x - c.x) / lb) * r, z: c.z + ((b.z - c.z) / lb) * r };
    for (let k = 0; k <= steps; k++) {
      const u = k / steps, w0 = (1 - u) * (1 - u), w1 = 2 * u * (1 - u), w2 = u * u;
      out.push({ x: w0 * p0.x + w1 * c.x + w2 * p2.x, z: w0 * p0.z + w1 * c.z + w2 * p2.z });
    }
  }
  const last = points[points.length - 1];
  out.push({ x: last.x, z: last.z });
  return out;
}

/** The yaw `yaw` turned toward `want` (radians) by at most `maxRate × dt`, the short way round. */
export function turnToward(yaw: number, want: number, maxRate: number, dt: number): number {
  const d = Math.atan2(Math.sin(want - yaw), Math.cos(want - yaw));
  const step = maxRate * dt;
  return Math.abs(d) <= step ? want : yaw + Math.sign(d) * step;
}

/** How far apart two yaws are (radians, 0 … π). */
export function yawGap(a: number, b: number): number { return Math.abs(Math.atan2(Math.sin(b - a), Math.cos(b - a))); }
